import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Trash2, Edit, Users, Clock, DollarSign, X, Loader2, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PartnerLayout } from '@/components/PartnerLayout';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const EVENT_TYPES = [
  { value: 'christmas', label: 'Christmas Party' }, { value: 'awards', label: 'Awards Ceremony' },
  { value: 'launch', label: 'Launch Party' }, { value: 'conference', label: 'Conference' },
  { value: 'holiday', label: 'Holiday Party' }, { value: 'team_building', label: 'Team Building' },
  { value: 'baby_shower', label: 'Baby Shower' }, { value: 'birthday', label: 'Birthday Party' },
  { value: 'wedding', label: 'Wedding Reception' }, { value: 'general', label: 'General Event' },
];

export default function PartnerPrivateHire() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [venues, setVenues] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSpaceDialog, setShowSpaceDialog] = useState(false);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [spaceForm, setSpaceForm] = useState({ name: '', description: '', capacity_min: 10, capacity_max: 50, size_sqft: '', features: [], images: [] });
  const [pkgForm, setPkgForm] = useState({ name: '', category: 'social', event_type: 'general', description: '', space_id: '', duration_hours: 4, duration_label: '4 Hours', base_price: 0, per_person_price: 0, min_spend: 0, min_guests: 10, max_guests: 50, includes: [], addons: [], images: [] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const venueRes = await api.get('/partner/venues');
      setVenues(venueRes.data || []);
      if (venueRes.data?.[0]) {
        const vid = venueRes.data[0].id;
        const [spaceRes, pkgRes] = await Promise.all([
          api.get(`/private-hire/spaces/${vid}`),
          api.get(`/private-hire/packages/${vid}`),
        ]);
        setSpaces(spaceRes.data || []);
        setPackages(pkgRes.data || []);
      }
    } catch {} finally { setLoading(false); }
  };

  const venueId = venues[0]?.id;

  const handleSaveSpace = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/private-hire/spaces/${editItem.id}`, spaceForm);
      } else {
        await api.post('/private-hire/spaces', { ...spaceForm, venue_id: venueId });
      }
      toast.success('Space saved');
      setShowSpaceDialog(false);
      setEditItem(null);
      fetchData();
    } catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const handleSavePackage = async () => {
    if (!pkgForm.name) { toast.error('Package name required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/private-hire/packages/${editItem.id}`, pkgForm);
      } else {
        await api.post('/private-hire/packages', { ...pkgForm, venue_id: venueId });
      }
      toast.success('Package saved');
      setShowPackageDialog(false);
      setEditItem(null);
      fetchData();
    } catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  if (loading) return <PartnerLayout title="Private Hire"><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div></PartnerLayout>;

  return (
    <PartnerLayout title="Private Hire" subtitle="Manage event spaces and packages">
      <Tabs defaultValue="packages">
        <TabsList>
          <TabsTrigger value="packages">Packages ({packages.length})</TabsTrigger>
          <TabsTrigger value="spaces">Event Spaces ({spaces.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="packages" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditItem(null); setPkgForm({ name: '', category: 'social', event_type: 'general', description: '', space_id: spaces[0]?.id || '', duration_hours: 4, duration_label: '4 Hours', base_price: 0, per_person_price: 0, min_spend: 0, min_guests: 10, max_guests: 50, includes: [], addons: [], images: [] }); setShowPackageDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" /> New Package
            </Button>
          </div>
          {packages.length === 0 ? (
            <Card className="p-12 text-center"><Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No packages yet. Create your first private hire package.</p></Card>
          ) : packages.map(pkg => (
            <Card key={pkg.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge className={pkg.category === 'corporate' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}>{pkg.category}</Badge>
                    <h3 className="font-bold">{pkg.name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{pkg.duration_label} · {pkg.min_guests}-{pkg.max_guests} guests · From ${pkg.base_price}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditItem(pkg); setPkgForm(pkg); setShowPackageDialog(true); }}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={async () => { await api.delete(`/private-hire/packages/${pkg.id}`); toast.success('Deleted'); fetchData(); }}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="spaces" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditItem(null); setSpaceForm({ name: '', description: '', capacity_min: 10, capacity_max: 50, size_sqft: '', features: [], images: [] }); setShowSpaceDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" /> New Space
            </Button>
          </div>
          {spaces.length === 0 ? (
            <Card className="p-12 text-center"><Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No event spaces. Add your rentable spaces first.</p></Card>
          ) : spaces.map(space => (
            <Card key={space.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-bold">{space.name}</h3>
                  <p className="text-sm text-muted-foreground">{space.capacity_min}-{space.capacity_max} guests{space.size_sqft ? ` · ${space.size_sqft} sq ft` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditItem(space); setSpaceForm(space); setShowSpaceDialog(true); }}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={async () => { await api.delete(`/private-hire/spaces/${space.id}`); toast.success('Deleted'); fetchData(); }}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Space Dialog */}
      <Dialog open={showSpaceDialog} onOpenChange={setShowSpaceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editItem ? 'Edit Space' : 'New Event Space'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Space Name *</Label><Input value={spaceForm.name} onChange={e => setSpaceForm({...spaceForm, name: e.target.value})} placeholder="e.g., Main Hall, Rooftop Terrace" /></div>
            <div><Label>Description</Label><Textarea value={spaceForm.description} onChange={e => setSpaceForm({...spaceForm, description: e.target.value})} rows={3} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Min Guests</Label><Input type="number" value={spaceForm.capacity_min} onChange={e => setSpaceForm({...spaceForm, capacity_min: parseInt(e.target.value)||0})} /></div>
              <div><Label>Max Guests</Label><Input type="number" value={spaceForm.capacity_max} onChange={e => setSpaceForm({...spaceForm, capacity_max: parseInt(e.target.value)||0})} /></div>
              <div><Label>Sq Ft</Label><Input type="number" value={spaceForm.size_sqft||''} onChange={e => setSpaceForm({...spaceForm, size_sqft: parseInt(e.target.value)||null})} /></div>
            </div>
            <div><Label>Features (comma separated)</Label><Input value={(spaceForm.features||[]).join(', ')} onChange={e => setSpaceForm({...spaceForm, features: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})} placeholder="AV system, stage, private bar" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSpaceDialog(false)}>Cancel</Button><Button onClick={handleSaveSpace} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editItem ? 'Edit Package' : 'New Private Hire Package'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Package Name *</Label><Input value={pkgForm.name} onChange={e => setPkgForm({...pkgForm, name: e.target.value})} placeholder="Corporate Half-Day" /></div>
              <div><Label>Event Space</Label>
                <Select value={pkgForm.space_id||'_none'} onValueChange={v => setPkgForm({...pkgForm, space_id: v==='_none'?'':v})}><SelectTrigger><SelectValue placeholder="Select space" /></SelectTrigger><SelectContent><SelectItem value="_none">Any Space</SelectItem>{spaces.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Category</Label><Select value={pkgForm.category} onValueChange={v => setPkgForm({...pkgForm, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="corporate">Corporate</SelectItem><SelectItem value="social">Social</SelectItem></SelectContent></Select></div>
              <div><Label>Event Type</Label><Select value={pkgForm.event_type} onValueChange={v => setPkgForm({...pkgForm, event_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{EVENT_TYPES.map(t=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Description</Label><Textarea value={pkgForm.description} onChange={e => setPkgForm({...pkgForm, description: e.target.value})} rows={3} /></div>
            
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Duration (hours)</Label><Input type="number" value={pkgForm.duration_hours} onChange={e => setPkgForm({...pkgForm, duration_hours: parseInt(e.target.value)||4})} /></div>
              <div><Label>Duration Label</Label><Input value={pkgForm.duration_label} onChange={e => setPkgForm({...pkgForm, duration_label: e.target.value})} placeholder="4 Hours / Half Day / Full Evening" /></div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
              <div><Label>Base Price ($)</Label><Input type="number" value={pkgForm.base_price} onChange={e => setPkgForm({...pkgForm, base_price: parseFloat(e.target.value)||0})} /></div>
              <div><Label>Per Person ($)</Label><Input type="number" value={pkgForm.per_person_price} onChange={e => setPkgForm({...pkgForm, per_person_price: parseFloat(e.target.value)||0})} /></div>
              <div><Label>Min Spend ($)</Label><Input type="number" value={pkgForm.min_spend} onChange={e => setPkgForm({...pkgForm, min_spend: parseFloat(e.target.value)||0})} /></div>
              <div className="grid grid-cols-2 gap-2"><div><Label>Min</Label><Input type="number" value={pkgForm.min_guests} onChange={e => setPkgForm({...pkgForm, min_guests: parseInt(e.target.value)||0})} /></div><div><Label>Max</Label><Input type="number" value={pkgForm.max_guests} onChange={e => setPkgForm({...pkgForm, max_guests: parseInt(e.target.value)||0})} /></div></div>
            </div>

            <div>
              <Label>What's Included</Label>
              {(pkgForm.includes||[]).map((item, i) => (
                <div key={i} className="flex gap-2 mt-1"><Input value={item} onChange={e => { const inc=[...pkgForm.includes]; inc[i]=e.target.value; setPkgForm({...pkgForm, includes: inc}); }} /><Button variant="ghost" size="sm" onClick={() => setPkgForm({...pkgForm, includes: pkgForm.includes.filter((_,idx)=>idx!==i)})}><X className="w-4 h-4 text-red-500" /></Button></div>
              ))}
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => setPkgForm({...pkgForm, includes: [...(pkgForm.includes||[]), '']})}><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>

            <div>
              <Label>Add-ons (optional extras)</Label>
              {(pkgForm.addons||[]).map((addon, i) => (
                <div key={i} className="flex gap-2 mt-1"><Input value={addon.name||''} placeholder="Name" onChange={e => { const a=[...pkgForm.addons]; a[i]={...a[i],name:e.target.value}; setPkgForm({...pkgForm, addons: a}); }} className="flex-1" /><Input type="number" value={addon.price||''} placeholder="$" onChange={e => { const a=[...pkgForm.addons]; a[i]={...a[i],price:parseFloat(e.target.value)||0}; setPkgForm({...pkgForm, addons: a}); }} className="w-24" /><Button variant="ghost" size="sm" onClick={() => setPkgForm({...pkgForm, addons: pkgForm.addons.filter((_,idx)=>idx!==i)})}><X className="w-4 h-4 text-red-500" /></Button></div>
              ))}
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => setPkgForm({...pkgForm, addons: [...(pkgForm.addons||[]), {name:'',price:0,description:''}]})}><Plus className="w-3 h-3 mr-1" /> Add-on</Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowPackageDialog(false)}>Cancel</Button><Button onClick={handleSavePackage} disabled={saving}>{saving ? 'Saving...' : 'Save Package'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PartnerLayout>
  );
}
